import { useState } from "react";
import type { ForteXApi, UserDto } from "@fortex/api";
import { Button, Dialog, Field, Input, Switch, errorMessage } from "@fortex/map";
import { passwordProblems } from "../../account/AccountPage";
import { useApp } from "../../app/context";
import type { AppLabels } from "../../i18n";
import type { FieldErrors } from "../shared/CrudPage";
import { UserRoleSwitches } from "./UserRoleSwitches";

export type UserDraft = { user: UserDto | null; email: string; fullName: string; phoneNumber: string; admin: boolean; active: boolean; password: string; confirmPassword: string };

export function draftOf(user: UserDto | null): UserDraft {
  return {
    user,
    email: user?.email ?? "",
    fullName: user?.fullName ?? "",
    phoneNumber: user?.phoneNumber ?? "",
    admin: user?.admin ?? false,
    active: user?.active ?? true,
    password: "",
    confirmPassword: ""
  };
}

export async function saveUser(api: ForteXApi, draft: UserDraft): Promise<UserDto> {
  if (draft.user === null) {
    return api.createUser({ email: draft.email.trim(), password: draft.password, confirmPassword: draft.confirmPassword, admin: draft.admin, fullName: draft.fullName.trim() || null, phoneNumber: draft.phoneNumber.trim() || null });
  }

  let saved = await api.updateUser(draft.user.id, { name: draft.user.name, email: draft.email.trim(), active: draft.active, fullName: draft.fullName.trim() || null, phoneNumber: draft.phoneNumber.trim() || null, newPassword: null, confirmNewPassword: null });

  if (draft.admin !== draft.user.admin) saved = await api.setUserAdmin(draft.user.id, draft.admin);

  return saved;
}

export type UserFormProps = {
  api: ForteXApi;
  labels: AppLabels;
  draft: UserDraft;
  setDraft: (draft: UserDraft) => void;
  errors: FieldErrors;
  canEditRoles: boolean;
  readOnly: boolean;
};

export function UserForm({ api, labels, draft, setDraft, errors, canEditRoles, readOnly }: UserFormProps) {
  const { toast } = useApp();
  const user = draft.user;
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPassword, setResetPassword] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  const runReset = async () => {
    if (user === null) return;

    const problem = passwordProblems(labels, resetPassword, resetPassword);

    if (problem !== null) {
      toast("danger", problem);
      return;
    }

    setResetBusy(true);

    try {
      await api.resetPassword(user.id, resetPassword);
      toast("success", labels.passwordReset);
      setResetOpen(false);
      setResetPassword("");
    } catch (reason) {
      toast("danger", errorMessage(reason, labels));
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <form className="fxapp-form" onSubmit={event => event.preventDefault()} noValidate data-fxmap="user-form">
      <section className="fxapp-form-section">
        <h3 className="fxapp-settings-title">{labels.sectionAccount}</h3>
        <div className="fxapp-form-row">
          <Field label={labels.email} required error={errors.email ?? null}>
            <Input type="email" value={draft.email} readOnly={readOnly} onChange={event => setDraft({ ...draft, email: event.target.value })} data-fxmap="user-email" />
          </Field>
          <Field label={labels.fullName}>
            <Input value={draft.fullName} readOnly={readOnly} onChange={event => setDraft({ ...draft, fullName: event.target.value })} />
          </Field>
          <Field label={labels.phoneNumber}>
            <Input type="tel" value={draft.phoneNumber} readOnly={readOnly} onChange={event => setDraft({ ...draft, phoneNumber: event.target.value })} />
          </Field>
        </div>
      </section>
      <section className="fxapp-form-section">
        <h3 className="fxapp-settings-title">{labels.sectionPassword}</h3>
        {user === null ? (
          <div className="fxapp-form-row">
            <Field label={labels.password} required error={errors.password === labels.passwordMinLength ? errors.password : null}>
              <Input type="password" autoComplete="new-password" value={draft.password} onChange={event => setDraft({ ...draft, password: event.target.value })} />
            </Field>
            <Field label={labels.confirmPassword} required error={errors.password === labels.passwordMismatch ? errors.password : null}>
              <Input type="password" autoComplete="new-password" value={draft.confirmPassword} onChange={event => setDraft({ ...draft, confirmPassword: event.target.value })} />
            </Field>
          </div>
        ) : (
          <div className="fxapp-toolbar">
            <Button kind="ghost" size="sm" label={labels.resetPassword} disabled={readOnly} onClick={() => setResetOpen(true)} data-fxmap="reset-password" />
          </div>
        )}
      </section>
      <section className="fxapp-form-section">
        <h3 className="fxapp-settings-title">{labels.sectionRoles}</h3>
        <div className="fxapp-form-row">
          <div className="fxapp-role-list">
            <Switch checked={draft.admin} disabled={readOnly} onChange={admin => setDraft({ ...draft, admin })} label={labels.administrator} showLabel />
            {user !== null ? <Switch checked={draft.active} disabled={readOnly || !user.editable} onChange={active => setDraft({ ...draft, active })} label={labels.active} showLabel /> : null}
          </div>
          {user !== null && canEditRoles ? <UserRoleSwitches api={api} labels={labels} userId={user.id} readOnly={readOnly} /> : null}
        </div>
      </section>
      <Dialog open={resetOpen} title={labels.resetPassword} confirmLabel={labels.save} cancelLabel={labels.cancel} busy={resetBusy} onConfirm={() => void runReset()} onCancel={() => setResetOpen(false)} dataFxmap="reset-password-dialog">
        <Field label={labels.newPassword} required hint={labels.passwordMinLength}>
          <Input type="password" autoComplete="new-password" value={resetPassword} onChange={event => setResetPassword(event.target.value)} />
        </Field>
      </Dialog>
    </form>
  );
}
